import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { warmGallery, warmImage, canWarmImages } from '@theme/mozef-image-loading';
import { isMobileBreakpoint, isRTL, prefersReducedMotion } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    // A newly selected colour inserts the parent before its slideshow child
    // upgrades. Bind after that upgrade, including already-defined elements.
    customElements.whenDefined('slideshow-component').then(() => {
      if (this.isConnected && !signal.aborted) this.#bindLoop(signal);
    });
  }

  #bindLoop(signal) {
    const scroller = this.slideshow?.refs?.scroller;
    scroller?.addEventListener('touchstart', this.#startLoop, { signal, passive: true });
    scroller?.addEventListener('touchmove', this.#moveLoop, { signal, passive: false });
    scroller?.addEventListener('touchend', this.#endLoop, { signal, passive: false });
    scroller?.addEventListener('touchcancel', this.#cancelLoop, { signal });
    scroller?.addEventListener('click', this.#suppressLoopClick, { signal, capture: true });
    this.slideshow?.addEventListener(SlideshowSelectEvent.eventName, (event) => {
      // Native momentum can report its selection after the next touch starts.
      // Only an explicit control selection should interrupt that new gesture.
      if (event.detail.trigger !== 'scroll') this.#clearLoop();
    }, { signal });
    window.addEventListener('resize', this.#cancelLoop, { signal });
    this.#loopWarmTimer = window.setTimeout(() => {
      if (!this.isConnected || !isMobileBreakpoint() || !canWarmImages()) return;
      const slides = this.slideshow?.slides;
      const lastImage = slides?.length > 1 && slides[slides.length - 1].querySelector('img');
      if (lastImage) warmImage(lastImage);
    }, 1000);
  }

  #controller = new AbortController();
  #selection = 0;
  #loop = null;
  #loopFrame = 0;
  #suppressClickUntil = 0;
  #loopWarmTimer = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    clearTimeout(this.#loopWarmTimer);
    this.#clearLoop();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const selection = ++this.#selection;
    this.setAttribute('aria-busy', 'true');

    event.promise
      .then(async ({ detail }) => {
        if (selection !== this.#selection || !this.isConnected) return;
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        await warmGallery(newMediaGallery, 'high');
        // A slower earlier colour must never replace the most recent choice.
        if (selection !== this.#selection || !this.isConnected) return;
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      })
      .finally(() => {
        if (selection === this.#selection) this.removeAttribute('aria-busy');
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.#clearLoop();
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  // Native scrolling already handles the middle of the product gallery. Only
  // take over a gesture that leaves an end, so page scrolling and browser zoom
  // retain their native behaviour everywhere else.
  #startLoop = (event) => {
    this.#suppressClickUntil = 0;
    if (this.#loop?.animating && event.touches.length === 1) {
      const loop = this.#loop;
      cancelAnimationFrame(this.#loopFrame);
      this.#loopFrame = 0;
      loop.animating = false;
      loop.startX = event.touches[0].clientX - loop.dx;
      loop.startY = event.touches[0].clientY;
      loop.samples = [{ x: loop.dx, time: performance.now() }];
      return;
    }
    this.#clearLoop();
    const slideshow = this.slideshow;
    const slides = slideshow?.slides;
    if (!isMobileBreakpoint() || event.touches.length !== 1 || !slides || slides.length < 2 ||
        (window.visualViewport?.scale || 1) > 1.01) return;
    const touch = event.touches[0];
    const scroller = slideshow.refs.scroller;
    const bounds = scroller.getBoundingClientRect();
    const center = bounds.left + bounds.width / 2;
    // Read the photo actually under the finger. The slideshow's debounced
    // current index still points at the previous photo during a native snap.
    const distances = slides.map((slide) => {
      const rect = slide.getBoundingClientRect();
      return Math.abs(rect.left + rect.width / 2 - center);
    });
    const index = distances.indexOf(Math.min(...distances));
    if (index !== 0 && index !== slides.length - 1) return;
    this.#loop = {
      startX: touch.clientX, startY: touch.clientY, index, scroller, slides,
      direction: isRTL() ? -1 : 1, step: index === 0 ? -1 : 1,
      target: index === 0 ? slides.length - 1 : 0,
      width: scroller.clientWidth, dx: 0, active: false,
      samples: [{ x: 0, time: performance.now() }],
    };
  };

  #moveLoop = (event) => {
    const loop = this.#loop;
    if (!loop || loop.animating) return;
    if (event.touches.length !== 1) return this.#cancelLoop();
    const touch = event.touches[0];
    const dx = touch.clientX - loop.startX;
    const dy = touch.clientY - loop.startY;
    if (!loop.active) {
      // Ignore sub-pixel finger jitter before deciding between a horizontal
      // gallery gesture and the browser's vertical page scroll.
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 4) return;
      if (Math.abs(dy) > Math.abs(dx) || dx * loop.direction * loop.step >= 0) {
        this.#clearLoop();
        return;
      }
      if (!event.cancelable) return this.#clearLoop();
      event.preventDefault();
      loop.active = true;
      const scrollerBounds = loop.scroller.getBoundingClientRect();
      loop.positions = loop.slides.map((slide) => {
        const rect = slide.getBoundingClientRect();
        return { left: rect.left - scrollerBounds.left, width: rect.width };
      });
      const first = loop.positions[0], last = loop.positions[loop.positions.length - 1];
      loop.span = Math.abs(last.left - first.left) + first.width;
      const current = loop.positions[loop.index];
      loop.origin = current.left + (current.width - loop.width) / 2;
      const left = loop.scroller.scrollLeft;
      loop.scroller.style.setProperty('scroll-snap-type', 'none');
      loop.scroller.style.setProperty('overflow-x', 'hidden');
      loop.scroller.style.setProperty('scroll-behavior', 'auto');
      loop.scroller.scrollTo({ left, behavior: 'instant' });
      // Include the still-visible outgoing photo when catching native inertia.
      // Reuse the original slides, including in galleries with only two photos.
      for (const slide of loop.slides) {
        slide.style.willChange = 'transform';
        slide.style.contentVisibility = 'visible';
      }
      for (const index of [loop.index, loop.target]) {
        const img = loop.slides[index].querySelector('img');
        if (img) { img.loading = 'eager'; img.decode().catch(() => {}); }
      }
    } else if (event.cancelable) event.preventDefault();
    loop.dx = loop.step * loop.direction < 0 ? Math.max(0, dx) : Math.min(0, dx);
    const now = performance.now();
    loop.samples.push({ x: loop.dx, time: now });
    loop.samples = loop.samples.filter((sample) => now - sample.time <= 100);
    this.#suppressClickUntil = now + 500;
    if (!this.#loopFrame) this.#loopFrame = requestAnimationFrame(this.#paintLoop);
  };

  #paintLoop = () => {
    this.#loopFrame = 0;
    const loop = this.#loop;
    if (!loop?.active) return;
    for (const [index, slide] of loop.slides.entries()) {
      const { left, width } = loop.positions[index];
      const shift = Math.round((loop.width / 2 - left - width / 2 - loop.dx) / loop.span) * loop.span;
      slide.style.transform = `translate3d(${loop.dx + shift}px, 0, 0)`;
    }
  };

  #endLoop = (event) => {
    const loop = this.#loop;
    if (!loop?.active) return this.#clearLoop();
    const now = performance.now();
    // Catching an animation and lifting again is still a swipe interaction,
    // even when that second touch did not move: suppress its synthetic click.
    if (event.cancelable) event.preventDefault();
    this.#suppressClickUntil = now + 500;
    const first = loop.samples.find((sample) => now - sample.time <= 100);
    const velocity = first ? (loop.dx - first.x) / Math.max(1, now - first.time) : 0;
    const flick = Math.abs(loop.dx) >= 14 && Math.abs(velocity) >= 0.45;
    const advance = flick ? velocity * loop.dx > 0 : Math.abs(loop.dx) >= loop.width * 0.2;
    this.#settleLoop(advance);
  };

  #settleLoop(advance) {
    const loop = this.#loop;
    if (!loop?.active) return this.#clearLoop();
    cancelAnimationFrame(this.#loopFrame);
    this.#paintLoop();
    loop.destination = advance ? loop.target : loop.index;
    const end = -loop.origin + (advance ? -loop.step * loop.width * loop.direction : 0);
    if (prefersReducedMotion()) return this.#finishLoop(loop.destination);
    loop.animating = true;
    const from = loop.dx, start = performance.now();
    // Cached geometry only: each frame writes transforms, without layout reads.
    // Circular positions are refreshed offscreen so a two-photo gallery never
    // exposes a gap when the outgoing photo is still partly visible.
    const tick = (now) => {
      if (this.#loop !== loop || !loop.animating) return;
      const progress = Math.min(1, (now - start) / 240);
      loop.dx = from + (end - from) * (1 - Math.pow(1 - progress, 3));
      this.#paintLoop();
      if (progress === 1) this.#finishLoop(loop.destination);
      else this.#loopFrame = requestAnimationFrame(tick);
    };
    this.#loopFrame = requestAnimationFrame(tick);
  }

  #finishLoop(index) {
    const loop = this.#loop;
    this.#clearLoop();
    this.slideshow?.select(index, undefined, { animate: false });
    // A scroll observer may already have selected the transformed destination.
    // Restore the real scroll position even if select() therefore does nothing.
    if (loop) {
      const scroller = loop.scroller.getBoundingClientRect();
      const slide = loop.slides[index].getBoundingClientRect();
      loop.scroller.scrollBy({
        left: slide.left + slide.width / 2 - scroller.left - scroller.width / 2,
        behavior: 'instant',
      });
    }
  }

  #clearLoop() {
    cancelAnimationFrame(this.#loopFrame);
    this.#loopFrame = 0;
    const loop = this.#loop;
    this.#loop = null;
    if (!loop?.active) return;
    for (const slide of loop.slides) {
      for (const property of ['transform', 'will-change', 'content-visibility']) slide.style.removeProperty(property);
    }
    for (const property of ['scroll-snap-type', 'overflow-x', 'scroll-behavior']) loop.scroller.style.removeProperty(property);
  }

  #cancelLoop = () => {
    if (this.#loop?.active) this.#finishLoop(this.#loop.index);
    else this.#clearLoop();
  };

  #suppressLoopClick = (event) => {
    if (performance.now() >= this.#suppressClickUntil) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
